import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountingDocumentManagementService } from '@app/core/services/http/accounting-document-management/accounting-document-management.service';
import { ToastHelperService } from '@app/core/services/helpers/toast-helper.service';
import { AccountingUploadEligibilityResponse } from '@interfaces/accounting-document-management/responses/accounting-upload-eligibility.response';
import { ClassifiedDocumentUploadResponse } from '@interfaces/accounting-document-management/responses/classified-document-upload.response';
import {
  DocumentCoverageResponse,
  RequiredWindowResponse,
  YearCoverageBlockResponse,
} from '@interfaces/accounting-document-management/responses/document-coverage.response';
import { DocumentFileLinkResponse } from '@interfaces/accounting-document-management/responses/document-file-link.response';
import {
  Observable,
  Subject,
  catchError,
  concatMap,
  finalize,
  from,
  map,
  of,
  takeUntil,
} from 'rxjs';
import { buildClassificationMessage } from '../utils/classification-message.util';

/**
 * Modo de visualização da tela unificada de cobertura: régua (padrão, exercícios empilhados em linha)
 * ou cartões (mesmos dados em grade). A Matriz do protótipo foi descartada por decisão de produto.
 */
export type StatementViewMode = 'regua' | 'cartoes';

/**
 * Período selecionado em qualquer trilha (DRE, Balanço ou Faturamento) — origem única da seleção da
 * tela unificada (chip de pendência, botão anual ou mês da barra). Mês de faturamento é `(m, m)`;
 * anual (DRE/Balanço) é sempre `(1, 12)`.
 */
export interface SelectedCoveragePeriod {
  year: number;
  documentType: 'Dre' | 'Balanco' | 'Faturamento';
  startMonth: number;
  endMonth: number;
}

/**
 * Envio classificado em andamento. `year` é o exercício em que o arquivo foi solto (o `hintYear`) — é
 * ele que permite pintar o estado "enviando" **apenas** na área daquele exercício, e não em todas:
 * a área de envio é renderizada uma por exercício, e um sinal booleano da tela inteira faria todos os
 * anos dizerem "enviando" ao mesmo tempo, mentindo sobre onde o arquivo caiu (bug 2895).
 *
 * `index`/`total` (1-based) posicionam o arquivo no lote, para a área poder dizer "arquivo 2 de 3".
 */
export interface ClassifiedUploadProgress {
  year: number;
  fileName: string;
  index: number;
  total: number;
}

/**
 * Estado da feature de gestão de documentos contábeis (`/dashboard/accounting-document-management`).
 *
 * A página (`AccountingDocumentManagementComponent`) foi reescrita para o modelo de cobertura
 * documental (PBI #2728) e consome exclusivamente os sinais/métodos abaixo, prefixados por
 * "cobertura"/`coverage` por herança do nome original desta classe — não há mais uma API legada
 * coexistindo aqui (a versão anterior desta doc mencionava uma "tela atual, uma célula por vez" que
 * já não existe; removida na revisão final da spec de cobertura de documentos contábeis, junto com os
 * sinais/métodos órfãos que ela descrevia).
 */
@Injectable()
export class AccountingDocumentManagementStateService {
  private readonly http = inject(AccountingDocumentManagementService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  private companyId = '';

  /**
   * `true` depois que a página que provê este serviço é destruída. Existe porque `takeUntilDestroyed`
   * **completa** o stream em vez de abortá-lo em silêncio: sem esta guarda, o resumo do lote seria
   * exibido na tela seguinte para um envio que foi cancelado no meio.
   */
  private destroyed = false;

  /**
   * Aborta o lote de envio em andamento. Emitido por {@link init}, ou seja, na troca de empresa: o
   * lote pertencia à empresa anterior, e deixá-lo correr faria o card da empresa nova anunciar o
   * arquivo da antiga — além de abrir espaço para um segundo lote simultâneo, porque o progresso
   * (que é a guarda de "um lote por vez") teria sido zerado com a fila ainda no ar.
   */
  private readonly batchReset$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => (this.destroyed = true));
  }

  // #region Cobertura documental (tela nova, PBI #2728)
  /** Resposta crua do `GET document-coverage`: janela obrigatória + cobertura por tipo. `null` até o primeiro carregamento. */
  readonly coverage = signal<DocumentCoverageResponse | null>(null);

  /** Flag de cache local — evita refetch redundante de `loadCoverage()` sem `force`. */
  readonly coverageLoaded = signal(false);

  readonly isLoadingCoverage = signal(false);

  /** Janela obrigatória (`requiredWindow`) da última cobertura carregada, ou `null` antes do primeiro carregamento. */
  readonly requiredWindow = computed<RequiredWindowResponse | null>(
    () => this.coverage()?.requiredWindow ?? null
  );

  /**
   * Modo de visualização da tela unificada. **Cartões é o padrão** — decisão de produto de 21/08/2026, que
   * substitui o default do protótipo (Régua). Com três trilhas por exercício, o cartão mantém cada exercício
   * como um bloco fechado e legível; a Régua continua disponível no toggle.
   */
  readonly viewMode = signal<StatementViewMode>('cartoes');

  /**
   * Exercícios da tela, já agrupados por trilha **pelo backend** — DRE, Balanço e Faturamento.
   * Substitui a fusão que a página fazia no cliente antes do backend passar a entregar `years` pronto.
   */
  readonly years = computed<YearCoverageBlockResponse[]>(() => this.coverage()?.years ?? []);

  /** Período selecionado (intervalo + trilha) em qualquer exercício. `null` = nada selecionado. */
  readonly selectedPeriod = signal<SelectedCoveragePeriod | null>(null);

  /**
   * Resultado da última classificação automática, para a régua mostrar a linha "Cog AI leu X e
   * classificou como Y" no exercício correspondente. Limpo a cada troca de empresa.
   */
  readonly lastClassification = signal<{
    year: number | null;
    result: ClassifiedDocumentUploadResponse;
    /**
     * Frase já traduzida e pronta para exibir. Fica no estado — e não é remontada pelo componente —
     * para o chip do exercício e o toast não poderem divergir no texto do mesmo envio.
     */
    message: string;
  } | null>(null);

  /**
   * Envio classificado em andamento (exercício + arquivo + posição no lote), ou `null` quando nada
   * está sendo enviado. Substituiu o booleano `isUploadingClassified` porque a tela precisa saber
   * **em qual exercício** e **qual arquivo** — sem isso não há como dar retorno visual honesto
   * (bug 2895). Quem só quer saber "tem envio rolando?" usa {@link isUploadingClassified}.
   */
  readonly classifiedUpload = signal<ClassifiedUploadProgress | null>(null);

  /** `true` enquanto um envio por exercício (com classificação automática) está em andamento. */
  readonly isUploadingClassified = computed(() => this.classifiedUpload() !== null);

  // #endregion

  // #region Gate de elegibilidade de envio (PBI #2728, 24/08/2026)
  /**
   * Resposta do gate `GET upload-eligibility`. `null` até o primeiro carregamento — enquanto isso a
   * página trata como liberado ({@link AccountingUploadEligibilityResponse.canUpload} `?? true`) para
   * não piscar o aviso de bloqueio na maioria das empresas, que já tem onboarding. Onboarding é
   * pré-requisito do envio: sem ele não há catálogo para o agente casar o documento.
   */
  readonly uploadEligibility = signal<AccountingUploadEligibilityResponse | null>(null);
  // #endregion

  /**
   * Inicializa o estado para a empresa selecionada: reseta o cache local de cobertura e a seleção.
   * Chamado sempre que a empresa muda — inclusive na troca pelo seletor do cabeçalho da página, que
   * precisa zerar a seleção de período (um período da empresa anterior não existe na nova cobertura).
   * O modo de visualização é preservado de propósito.
   *
   * O nome de exibição da empresa não é resolvido aqui: quem mostra o nome é o próprio seletor da
   * página, a partir da lista de empresas que ele já carrega.
   */
  init(companyId: string): void {
    this.companyId = companyId;

    this.coverage.set(null);
    this.coverageLoaded.set(false);
    this.selectedPeriod.set(null);
    this.lastClassification.set(null);
    this.uploadEligibility.set(null);
    // Aborta o lote da empresa anterior. É o `batchReset$` que zera o progresso, pelo `finalize` do
    // pipe — zerar o sinal aqui sem parar a fila destravaria as áreas com o lote ainda correndo e
    // permitiria um segundo envio simultâneo, porque é o próprio progresso que serve de guarda.
    this.batchReset$.next();
  }

  setViewMode(mode: StatementViewMode): void {
    this.viewMode.set(mode);
  }

  /** Seleciona um período (chip de pendência, botão anual da trilha ou mês da barra de faturamento). */
  selectPeriod(period: SelectedCoveragePeriod): void {
    this.selectedPeriod.set(period);
  }

  clearSelection(): void {
    this.selectedPeriod.set(null);
  }

  /**
   * Envia um lote de arquivos **sem período** para um exercício: a Cog AI decide exercício, intervalo
   * e trilha de cada um.
   *
   * **Fila sequencial, um POST por vez** (decisão de 08/09/2026, bug 2895): o endpoint classifica um
   * arquivo por chamada, e até aqui a área de envio pegava só `files[0]` e **descartava o resto em
   * silêncio** — arrastar três documentos enviava um e não avisava nada. Agora todos entram, em ordem.
   *
   * Falha de um item **não aborta** o lote: cada arquivo é independente e abortar obrigaria a reenviar
   * o que já tinha dado certo. Quando a IA não consegue decidir (`needsHumanReview`), o backend **não
   * arquiva nada** — isso vira aviso, não sucesso, para o usuário não achar que o documento entrou na
   * cobertura.
   *
   * A cobertura é recarregada **uma vez só, no fim**, e não a cada arquivo: são N refetches de
   * `document-coverage` à toa, e a tela ainda mudaria debaixo do usuário no meio do lote.
   */
  sendClassifiedFiles(files: readonly File[], hintYear: number): void {
    const batch = Array.from(files);
    // Envio em andamento bloqueia novo lote: o progresso é um sinal único e dois lotes concorrentes
    // deixariam a área anunciando um arquivo enquanto outro sobe.
    if (batch.length === 0 || this.classifiedUpload() !== null) return;

    // A empresa é fixada **no início do lote**, não lida a cada item: `this.companyId` muda quando o
    // seletor da página troca de empresa (`init`), e a fila segura cada arquivo por segundos até a Cog
    // AI responder. Lendo tarde, trocar de empresa no meio do lote mandaria os arquivos seguintes para
    // a empresa nova — com o exercício da antiga. Antes da fila não havia essa janela.
    const companyId = this.companyId;

    let sent = 0;
    let failed = 0;
    let review = 0;
    const single = batch.length === 1;

    from(batch.map((file, index) => ({ file, index })))
      .pipe(
        concatMap(({ file, index }) => {
          this.classifiedUpload.set({
            year: hintYear,
            fileName: file.name,
            index: index + 1,
            total: batch.length,
          });

          // `catchError` por item, dentro do `concatMap`: sem isso o primeiro erro encerraria o
          // observable e mataria a fila (é o comportamento que a decisão D3 rejeitou).
          return this.http.uploadClassifiedDocument(companyId, file, hintYear).pipe(
            map(res => ({ file, res, error: null as unknown })),
            catchError((error: unknown) => of({ file, res: null, error }))
          );
        }),
        // `takeUntil` antes do `takeUntilDestroyed`: troca de empresa aborta o lote (ver `batchReset$`),
        // destruição da página aborta tudo. Nos dois casos o `finalize` limpa o progresso.
        takeUntil(this.batchReset$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.classifiedUpload.set(null))
      )
      .subscribe({
        next: ({ file, res, error }) => {
          if (error) {
            failed++;
            if (single)
              this.toast.showToastError(this.describeError(error, 'Erro ao enviar documento.'));
            return;
          }
          // Resposta vazia sem erro HTTP conta como **falha**: sem isso o item não entrava em nenhum
          // contador e o resumo do lote virava um "1 de 3 documentos enviados" em verde, sem dizer o
          // que houve com os outros dois.
          if (!res) {
            failed++;
            if (single) this.toast.showToastError('Erro ao enviar documento.');
            return;
          }

          const message = buildClassificationMessage(res, file.name);
          this.lastClassification.set({
            year: hintYear,
            result: res,
            message,
          });

          if (res.needsHumanReview) {
            review++;
            if (single) {
              // O fallback não pode mandar o usuário "enviar pelo período específico", como fazia até
              // 21/08/2026: aquele caminho (envio travado pela célula selecionada) foi removido junto com o
              // `period-upload`, e instruir um caminho inexistente é pior que não instruir nada. O que
              // resolve de fato é o documento — reenviar a página/o recorte que declara exercício e período.
              this.toast.showToastWarn(
                res.reviewReason ??
                  'A Cog AI não conseguiu identificar o período do documento. Nada foi arquivado — ' +
                    'confira se o arquivo declara exercício e período e envie novamente.'
              );
            }
            return;
          }

          sent++;
          // A frase vem de `buildClassificationMessage`, não de `res.summary`: o resumo do backend carrega a
          // chave crua do item do catálogo (`BalanceSheetIncomeStatementCashFlow`), e é o utilitário que a
          // traduz para o rótulo que o usuário já vê no onboarding. O sufixo "Aguardando aprovação" (Task 5
          // do PBI 2728) também sai de lá, pelo mesmo motivo de não duplicar texto entre toast e chip: sem
          // auto-aprovação a submissão nasce Pending e nada é copiado/enfileirado ainda.
          if (single) this.toast.showToastSuccess(message);
        },
        complete: () => {
          // Lote cancelado no meio não tem resumo. `takeUntil`/`takeUntilDestroyed` **completam** o
          // stream em vez de abortá-lo em silêncio, então este `complete` roda igual — e o toast falaria
          // de um envio interrompido, já na tela (ou na empresa) seguinte. Todo item emitido incrementa
          // exatamente um dos três contadores, logo "soma < total" é precisamente "a fila não drenou".
          if (this.destroyed || sent + review + failed < batch.length) return;

          // Lote com mais de um arquivo troca o toast por arquivo por um resumo: uma pilha de N toasts
          // esconde justamente o que o usuário precisa saber — quantos documentos entraram de fato.
          // Verde só quando **todos** entraram: `sent < total` significa que algo ficou pelo caminho.
          if (!single)
            this.toast[sent < batch.length ? 'showToastWarn' : 'showToastSuccess'](
              this.describeBatch(batch.length, sent, review, failed)
            );

          if (sent > 0) this.loadCoverage(true);
        },
      });
  }

  /** Resumo de um lote de envio classificado, para o toast único do fim da fila. */
  private describeBatch(total: number, sent: number, review: number, failed: number): string {
    const parts = [`${sent} de ${total} documentos enviados`];
    if (review > 0) {
      parts.push(
        `${review} sem período identificado pela Cog AI (nada arquivado, confira se o arquivo ` +
          'declara exercício e período)'
      );
    }
    if (failed > 0) parts.push(`${failed} com erro no envio`);

    return parts.join('; ') + '.';
  }

  // #region Cobertura documental (tela nova, PBI #2728)

  /**
   * Carrega a cobertura documental consolidada (`GET document-coverage`) — os três tipos de documento
   * numa única chamada. No-op se já carregada e `force` for `false`.
   */
  loadCoverage(force = false): void {
    if (this.coverageLoaded() && !force) return;
    this.isLoadingCoverage.set(true);
    this.http
      .getDocumentCoverage(this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.coverage.set(res);
          this.coverageLoaded.set(true);
          this.isLoadingCoverage.set(false);
        },
        error: (err: unknown) => {
          this.toast.showToastError(
            this.describeError(err, 'Erro ao carregar cobertura documental.')
          );
          this.isLoadingCoverage.set(false);
        },
      });
  }

  // #region Gate de elegibilidade de envio (PBI #2728, 24/08/2026)
  /**
   * Carrega o gate de elegibilidade (`GET upload-eligibility`), chamado uma vez por empresa junto com
   * {@link loadCoverage}. Sem guarda de cache proposital — diferente da cobertura, esta resposta não
   * tem pipeline assíncrono por trás (o backend só olha se existe `OnboardingProcess` com documento
   * exigido), então não há "ficar desatualizado" a evitar, e o único chamador (`initCompany` da
   * página) já roda uma vez por troca de empresa.
   *
   * Falha aqui **não** deve travar a tela nem impedir a cobertura de aparecer: o consumidor
   * (`uploadEligibility() ?? true` no computed da página) trata "ainda não sei" como liberado. Este
   * gate é só a experiência desta tela (evitar o envio óbvio sem sentido); o casamento real do
   * documento contra o catálogo de documentos esperados do onboarding é responsabilidade de outra
   * parte da mesma spec (Task 4), não deste sinal.
   */
  loadUploadEligibility(): void {
    this.http
      .getUploadEligibility(this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.uploadEligibility.set(res),
      });
  }
  // #endregion

  /**
   * Exclui (soft delete) um documento. A confirmação do usuário (`ConfirmationService`) é
   * responsabilidade do componente que chama este método — o estado só executa.
   */
  deleteFile(extractionId: string): void {
    this.http
      .deleteDocument(this.companyId, extractionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.showToastSuccess('Documento removido do sistema.');
          this.loadCoverage(true);
        },
        error: (err: unknown) => {
          this.toast.showToastError(this.describeError(err, 'Erro ao excluir documento.'));
        },
      });
  }

  /**
   * Resolve o link de download de um arquivo, sob demanda (no clique). Não mexe em nenhum sinal de
   * estado — quem chama decide o que fazer com o link (ex.: abrir `FilePreviewModalComponent`).
   */
  getDownloadLink(extractionId: string): Observable<DocumentFileLinkResponse | null> {
    return this.http.getDocumentFileLink(this.companyId, extractionId);
  }
  // #endregion

  /**
   * Extrai a mensagem de negócio que o backend mandou no `ResultObject.errors[]` (o `VaultApiService`
   * transforma a resposta com `status: false` num `ErrorResponse`). Sem isso o usuário só via um texto
   * genérico. Cai no texto genérico quando o erro é técnico (rede, 500), que não ajuda a mostrar cru.
   * Mesmo padrão de `AccountingDataStandardizationStateService.describeError`.
   *
   * Público porque a página também precisa dele: o link do arquivo é resolvido no clique pela própria
   * página (`openFile`), e "documento sem arquivo" é uma mensagem de negócio que só o backend sabe
   * redigir — o texto genérico da página escondia a causa.
   */
  describeError(err: unknown, fallback: string): string {
    const errors = (err as { errors?: { message?: string }[] } | null)?.errors;
    const messages = (errors ?? []).map(item => item?.message).filter((m): m is string => !!m);

    return messages.length > 0 ? messages.join(' ') : fallback;
  }
}
